import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { ASSETS_BASE_URL } from '@/config';
import { ImageUpload } from '@/features/files';
import { isLinkedInUrl, isValidUrl, nullableStringSetter, urlSetter } from '@/helpers';
import { useCountries } from '@/hooks';

import { useUploadLogo } from '../../hooks';
import { CompanySchema } from '../../types';

export const SHORT_DESCRIPTION_MAX = 200;

export type ProfileHeaderFormFields = {
  name: string;
  website: string | null;
  linkedin: string | null;
  logo: string | null;
  short_description: string | null;
  hq_country: string | null;
};

export type ProfileHeaderFormProps = {
  readonly id: string;
  readonly company: CompanySchema;
  readonly onSubmit: (data: ProfileHeaderFormFields) => void;
};

export function ProfileHeaderForm({ id, company, onSubmit }: ProfileHeaderFormProps) {
  const { t } = useTranslation(['settings']);
  const uploadLogo = useUploadLogo({ id: company.id });
  const { options: countryOptions } = useCountries();

  const options = useMemo(
    () => ({
      defaultValues: {
        name: company.name ?? '',
        website: company.website ?? null,
        linkedin: company.linkedin ?? null,
        logo: company.logo ?? null,
        short_description: company.short_description ?? null,
        hq_country: company.hq_country ?? null,
      },
    }),
    [company]
  );

  return (
    <Form<ProfileHeaderFormFields> id={id} onSubmit={onSubmit} options={options}>
      {({ formState, register, watch, setValue }) => {
        const shortLength = (watch('short_description') ?? '').length;

        return (
          <div className="flex flex-col gap-6">
            <div className="shrink-0 sm:w-44">
              <Field id="logo" label={t('Logo')} classes={{ field: 'space-y-1' }}>
                <ImageUpload
                  baseUrl={ASSETS_BASE_URL}
                  value={watch('logo')}
                  onSelectFile={(file) => {
                    uploadLogo(file, (uploaded) => {
                      setValue('logo', uploaded.id);
                    });
                  }}
                />
              </Field>
            </div>

            <div className="flex-1 space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  id="name"
                  label={t('Commercial name')}
                  error={formState.errors['name']}
                  classes={{ field: 'space-y-1' }}
                >
                  <Input
                    id="name"
                    color={formState.errors['name'] ? 'error' : 'primary'}
                    registration={register('name', {
                      required: t('This field is required'),
                      setValueAs: nullableStringSetter,
                    })}
                  />
                </Field>

                <Field id="hq_country" label={t('HQ country')} classes={{ field: 'space-y-1' }}>
                  <Select
                    id="hq_country"
                    options={countryOptions}
                    placeholder={t('Select a country')}
                    registration={register('hq_country')}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  id="website"
                  label={t('Website')}
                  error={formState.errors['website']}
                  classes={{ field: 'space-y-1' }}
                >
                  <Input
                    id="website"
                    placeholder="e.g. https://www.nteaser.com"
                    color={formState.errors['website'] ? 'error' : 'primary'}
                    registration={register('website', {
                      setValueAs: urlSetter,
                      validate: (value) => isValidUrl(value) || t('Enter a valid URL'),
                    })}
                  />
                </Field>

                <Field
                  id="linkedin"
                  label={t('LinkedIn')}
                  error={formState.errors['linkedin']}
                  classes={{ field: 'space-y-1' }}
                >
                  <Input
                    id="linkedin"
                    placeholder="e.g. linkedin.com/company/your-company"
                    color={formState.errors['linkedin'] ? 'error' : 'primary'}
                    registration={register('linkedin', {
                      setValueAs: urlSetter,
                      validate: {
                        url: (value) => isValidUrl(value) || t('Enter a valid URL'),
                        linkedin: (value) => isLinkedInUrl(value) || t('Enter a LinkedIn URL'),
                      },
                    })}
                  />
                </Field>
              </div>

              <Field
                id="short_description"
                label={t('Short description')}
                error={formState.errors['short_description']}
                classes={{ field: 'space-y-1' }}
              >
                <TextArea
                  id="short_description"
                  lines={2}
                  placeholder={t('One line, shown under your name')}
                  registration={register('short_description', {
                    maxLength: {
                      value: SHORT_DESCRIPTION_MAX,
                      message: t('Maximum {{max}} characters', { max: SHORT_DESCRIPTION_MAX }),
                    },
                  })}
                />
                <Typography
                  as="p"
                  size="body-3"
                  weight="normal"
                  color={shortLength > SHORT_DESCRIPTION_MAX ? 'error' : 'secondary'}
                  className="text-right"
                >
                  {shortLength}/{SHORT_DESCRIPTION_MAX}
                </Typography>
              </Field>
            </div>
          </div>
        );
      }}
    </Form>
  );
}
